import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  Table,
  FormGroup,
  Collapse,
  Badge,
} from "reactstrap";
import PropTypes from "prop-types";

/**
 * Componente para el formulario de Impresión en una orden de producción
 * 
 * @param {Object} props - Props del componente
 * @param {Array} props.data - Datos de impresión (múltiples papeles)
 * @param {Function} props.onChange - Handler para cambios en el formulario
 * @param {Array} props.materiales - Lista de materiales disponibles
 * @param {Array} props.medidas - Lista de medidas disponibles
 * @param {Array} props.parametrosTinta - Parámetros de tinta seleccionados en planeación
 * @param {Function} props.onValidationError - Handler para errores de validación
 * @returns {React.Component}
 */
const Impresion = ({ data, onChange, materiales, medidas, parametrosTinta = [], onValidationError }) => {
  // Función para obtener el nombre de la medida a partir de su ID
  const getMedidaNombre = (tamanoId) => {
    if (!tamanoId) return "";
    const medida = medidas.find(m => m.id === tamanoId);
    return medida ? medida.nombre : "";
  };

  // Estado para manejar los múltiples papeles de impresión
  const [impresiones, setImpresiones] = useState(() => {
    console.log("Inicializando impresiones con data:", data);
    
    // Inicializar con los datos proporcionados, o con un valor predeterminado si no hay datos
    return Array.isArray(data) && data.length > 0
      ? data
      : [{
          // ID will be assigned by PocketBase
          tipo_papel: "",
          parametros: [{ 
            // ID will be assigned by PocketBase
            impresos: "", 
            sobrantes: "", 
            total: "",
            tamano: "",
            pasadas: "",
            tinta_color: ""
          }],
          isOpen: true // Para controlar el colapso/expansión de esta impresión
        }];
  });

  // Efecto para actualizar el estado cuando cambian los datos externos
  useEffect(() => {
    console.log("data cambió:", data);
    if (Array.isArray(data)) {
      if (data.length > 0) {
        console.log("Actualizando impresiones con nuevos datos:", data);
        
        // Los datos que recibimos ya están formateados para el componente de impresión
        // y contienen la estructura tipo_papel y parametros necesaria
        setImpresiones(data);
      } else {
        // Si data está vacío, establecemos al menos un elemento vacío
        console.log("data está vacío, inicializando con valores predeterminados");
        setImpresiones([{
          tipo_papel: "",
          parametros: [{
            impresos: "",
            sobrantes: "",
            total: "",
            tamano: "",
            pasadas: "",
            tinta_color: "",
            montaje: ""
          }],
          isOpen: true
        }]);
      }
    }
  }, [data]);

  // Efecto para pre-seleccionar tintas cuando hay parametrosTinta disponibles
  useEffect(() => {
    if (parametrosTinta && parametrosTinta.length > 0) {
      // Función auxiliar para obtener parámetros de tinta seleccionados
      const getParametrosDisponibles = () => {
        return parametrosTinta.map(parametro => {
          let tintasText = '';
          if (parametro.expand?.tinta && Array.isArray(parametro.expand.tinta)) {
            // Ordenar tintas C, M, Y, K
            const order = ['C', 'M', 'Y', 'K'];
            const tintasOrdenadas = parametro.expand.tinta.sort((a, b) => {
              const aIndex = order.indexOf(a.nombre);
              const bIndex = order.indexOf(b.nombre);
              if (aIndex !== -1 && bIndex !== -1) { return aIndex - bIndex; }
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              return a.nombre.localeCompare(b.nombre);
            });
            
            const tintasNombres = tintasOrdenadas.map(tinta => tinta.nombre).join(', ');
            tintasText = ` [${tintasNombres}]`;
          }
          
          return {
            id: parametro.id,
            nombre: parametro.nombre,
            displayText: `${parametro.nombre}${tintasText}`,
            tintas: parametro.expand?.tinta || []
          };
        });
      };
      
      const parametrosDisponibles = getParametrosDisponibles();
      
      if (parametrosDisponibles.length > 0) {
        setImpresiones(prevImpresiones => {
          const nuevasImpresiones = prevImpresiones.map(impresion => ({
            ...impresion,
            parametros: impresion.parametros.map(parametro => {
              // Solo pre-llenar si tinta_color está vacío o no tiene tintas
              const tintasActuales = parseTintasFromText(parametro.tinta_color || '');
              
              if (tintasActuales.length === 0) {
                // Pre-llenar con todas las tintas disponibles de los parametros seleccionados
                const todasLasTintas = parametrosDisponibles.map(p => p.displayText);
                return {
                  ...parametro,
                  tinta_color: formatTintasToText(todasLasTintas)
                };
              }
              
              return parametro;
            })
          }));
          
          // Notificar cambios al componente padre si hubo cambios
          const huboCambios = JSON.stringify(prevImpresiones) !== JSON.stringify(nuevasImpresiones);
          if (huboCambios) {
            setTimeout(() => {
              onChange({
                target: {
                  name: "impresion_data",
                  value: nuevasImpresiones
                }
              });
            }, 0);
          }
          
          return nuevasImpresiones;
        });
      }
    }
  }, [parametrosTinta, onChange]); // Solo ejecutar cuando cambien los parametrosTinta

  // Efecto para notificar cambios al componente padre
  useEffect(() => {
    // Solo notificar cambios si hay cambios en las impresiones y no al renderizar inicialmente
    const handleChange = () => {
      onChange({
        target: {
          name: "impresion_data",
          value: impresiones,
        },
      });
    };

    // Usamos una referencia para el último valor para evitar notificaciones innecesarias
    handleChange();
  }, [impresiones]); // Eliminamos onChange de las dependencias para evitar el bucle infinito

  // Handler para manejar cambios en el tipo de papel de una impresión
  const handlePapelChange = (impresionIndex, value) => {
    const newImpresiones = [...impresiones];
    newImpresiones[impresionIndex].tipo_papel = value;
    setImpresiones(newImpresiones);
  };

  // Handler para manejar cambios en los parámetros (filas de la tabla)
  const handleParametroChange = (impresionIndex, paramIndex, field, value) => {
    const newImpresiones = [...impresiones];
    newImpresiones[impresionIndex].parametros[paramIndex][field] = value;
    
    // Si se cambió impresos o sobrantes, calculamos el total automáticamente
    if (field === 'impresos' || field === 'sobrantes') {
      const parametro = newImpresiones[impresionIndex].parametros[paramIndex];
      const impresos = field === 'impresos' ? value : parametro.impresos;
      const sobrantes = field === 'sobrantes' ? value : parametro.sobrantes;
      
      const impresosNum = parseInt(impresos) || 0;
      const sobrantesNum = parseInt(sobrantes) || 0;
      
      newImpresiones[impresionIndex].parametros[paramIndex].total = (impresosNum + sobrantesNum).toString();
    }
    
    // Actualizar el estado local
    setImpresiones(newImpresiones);
    
    // Cuando se actualizan los datos de impresión, necesitamos notificar al componente padre
    // para que sincronice estos cambios con la estructura de corte_conversion
    onChange({
      target: {
        name: "impresion_data",
        value: newImpresiones
      }
    });
  };

  // La función handleAddParametro ha sido eliminada porque ahora las filas solo se pueden agregar desde Corte y Conversión

  // La función handleRemoveParametro ha sido eliminada porque ahora los parámetros solo se pueden eliminar desde Corte y Conversión
  
  // La función handleAddPapel ha sido eliminada porque ahora los papeles solo se pueden agregar desde Corte y Conversión
  
  // La función handleRemovePapel ha sido eliminada porque ahora los papeles solo se pueden eliminar desde Corte y Conversión
  
  // Controlar la expansión/colapso de una impresión
  const toggleImpresion = (index) => {
    const newImpresiones = [...impresiones];
    newImpresiones[index].isOpen = !newImpresiones[index].isOpen;
    setImpresiones(newImpresiones);
  };

  // Buscar el nombre del material según su ID
  const getMaterialName = (materialId) => {
    const material = materiales.find(m => m.id === materialId);
    return material ? `${material.nombre}${material.texto_medida ? ` - ${material.texto_medida}` : ''}` : "";
  };

  // Buscar el nombre de la medida según su ID
  const getMedidaName = (medidaId) => {
    const medida = medidas.find(m => m.id === medidaId);
    return medida ? medida.nombre : "";
  };

  // Función para obtener los parámetros de tinta seleccionados en planeación
  const getParametrosTintaSeleccionados = () => {
    console.log('getParametrosTintaSeleccionados - parametrosTinta:', parametrosTinta);
    
    return parametrosTinta.map(parametro => {
      let tintasText = '';
      if (parametro.expand?.tinta && Array.isArray(parametro.expand.tinta)) {
        // Ordenar tintas C, M, Y, K
        const order = ['C', 'M', 'Y', 'K'];
        const tintasOrdenadas = parametro.expand.tinta.sort((a, b) => {
          const aIndex = order.indexOf(a.nombre);
          const bIndex = order.indexOf(b.nombre);
          if (aIndex !== -1 && bIndex !== -1) { return aIndex - bIndex; }
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.nombre.localeCompare(b.nombre);
        });
        
        const tintasNombres = tintasOrdenadas.map(tinta => tinta.nombre).join(', ');
        tintasText = ` [${tintasNombres}]`;
      }
      
      return {
        id: parametro.id,
        nombre: parametro.nombre,
        displayText: `${parametro.nombre}${tintasText}`,
        tintas: parametro.expand?.tinta || []
      };
    });
  };

  // Función para convertir texto de tintas a array de líneas
  const parseTintasFromText = (text) => {
    if (!text) return [];
    return text.split('\n').filter(line => line.trim()).map(line => line.trim());
  };

  // Función para convertir array de líneas a texto de tintas
  const formatTintasToText = (tintasArray) => {
    return tintasArray.join('\n');
  };

  // Función para agregar una línea de tinta
  const addTintaLine = (impresionIndex, paramIndex) => {
    const newImpresiones = [...impresiones];
    const currentTintas = parseTintasFromText(newImpresiones[impresionIndex].parametros[paramIndex].tinta_color || '');
    const parametrosDisponibles = getParametrosTintaSeleccionados();
    
    // Agregar el primer parámetro disponible que no esté ya seleccionado
    const parametroToAdd = parametrosDisponibles.find(parametro => !currentTintas.includes(parametro.displayText));
    if (parametroToAdd) {
      currentTintas.push(parametroToAdd.displayText);
      newImpresiones[impresionIndex].parametros[paramIndex].tinta_color = formatTintasToText(currentTintas);
      setImpresiones(newImpresiones);
      
      // Notificar cambios al componente padre
      onChange({
        target: {
          name: "impresion_data",
          value: newImpresiones
        }
      });
    }
  };

  // Función para eliminar una línea de tinta
  const removeTintaLine = (impresionIndex, paramIndex, lineIndex) => {
    const newImpresiones = [...impresiones];
    const currentTintas = parseTintasFromText(newImpresiones[impresionIndex].parametros[paramIndex].tinta_color || '');
    currentTintas.splice(lineIndex, 1);
    newImpresiones[impresionIndex].parametros[paramIndex].tinta_color = formatTintasToText(currentTintas);
    setImpresiones(newImpresiones);
    
    // Notificar cambios al componente padre
    onChange({
      target: {
        name: "impresion_data",
        value: newImpresiones
      }
    });
  };

  // Función para cambiar una línea de tinta
  const changeTintaLine = (impresionIndex, paramIndex, lineIndex, newTinta) => {
    const newImpresiones = [...impresiones];
    const currentTintas = parseTintasFromText(newImpresiones[impresionIndex].parametros[paramIndex].tinta_color || '');
    currentTintas[lineIndex] = newTinta;
    
    // Filtrar tintas vacías para evitar que se guarden líneas vacías
    const filteredTintas = currentTintas.filter(tinta => tinta && tinta.trim() !== '');
    
    
    newImpresiones[impresionIndex].parametros[paramIndex].tinta_color = formatTintasToText(filteredTintas);
    setImpresiones(newImpresiones);
    
    // Notificar cambios al componente padre
    onChange({
      target: {
        name: "impresion_data",
        value: newImpresiones
      }
    });
  };

  // Método para validar los datos antes de guardar
  useEffect(() => {
    if (typeof onValidationError === 'function') {
      // Primero identificamos impresiones que realmente tienen datos en sus parámetros
      const impresionesConDatos = Array.isArray(impresiones) ? impresiones.filter(imp => {
        // Verificar si hay algún parámetro con datos significativos
        const hayParametrosConDatos = Array.isArray(imp.parametros) && imp.parametros.some(param => 
          param.impresos || param.sobrantes || param.tamano || param.pasadas || param.tinta_color
        );
        return hayParametrosConDatos;
      }) : [];
      
      // Si hay impresiones con datos reales, verificamos que todas tengan tipo de papel
      if (impresionesConDatos.length > 0) {
        const impresionSinPapel = impresionesConDatos.some(imp => !imp.tipo_papel);
        
        if (impresionSinPapel) {
          // Solo reportamos error si realmente hay impresiones con datos pero sin papel
          onValidationError({
            component: 'impresion',
            message: 'Debe seleccionar un tipo de papel para todas las impresiones'
          });
        } else {
          // Si no hay errores, limpiamos los errores anteriores
          onValidationError({
            component: 'impresion',
            message: null
          });
        }
      } else {
        // Si no hay impresiones con datos significativos, limpiamos los errores
        onValidationError({
          component: 'impresion',
          message: null
        });
      }
    }
  }, [impresiones, onValidationError]);

  return (
    <Card className="mb-3 border border-4 border-light">
      <CardBody>
        <h4 className="card-title mb-3">Impresión</h4>
        
        {/* Múltiples papeles de impresión */}
        {impresiones.map((impresion, impresionIndex) => (
          <div key={impresion.id} className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2 bg-light p-2">
              <div className="d-flex align-items-center">
                <Button
                  color="link"
                  size="sm"
                  onClick={() => toggleImpresion(impresionIndex)}
                  className="p-0 me-2"
                >
                  <i className={`fas fa-chevron-${impresion.isOpen ? 'down' : 'right'}`}></i>
                </Button>
                <h5 className="mb-0">
                  Papel {impresionIndex + 1}
                  {impresion.tipo_papel && (
                    <Badge color="info" className="ms-2">
                      {getMaterialName(impresion.tipo_papel)}
                    </Badge>
                  )}
                </h5>
              </div>
              {/* El div con el botón de eliminar ha sido completamente removido */}
            </div>
            
            <Collapse isOpen={impresion.isOpen}>
              {/* Tipo de papel */}
              <Row className="mb-3">
                <Col md="12">
                  <FormGroup>
                    <Label className="form-label">Tipo de Papel</Label>
                    <Input
                      type="select"
                      className="form-select"
                      value={impresion.tipo_papel || ""}
                      onChange={(e) => handlePapelChange(impresionIndex, e.target.value)}
                      disabled={true} // Deshabilitamos la selección de papel en impresión
                    >
                      <option value="">Seleccionado automáticamente desde Corte y Conversión</option>
                      {materiales.map((material) => (
                        <option key={`impresion-papel-${impresionIndex}-${material.id}`} value={material.id}>
                          {material.nombre} {material.texto_medida ? `- ${material.texto_medida}` : ''}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              
              {/* Tabla de impresión */}
              <Row className="mb-3">
                <Col md="12">
                  <div className="table-responsive">
                    <Table className="table-bordered">
                      <thead>
                        <tr>
                          <th style={{ width: "5%" }} rowSpan="2">N.º</th>
                          <th style={{ width: "10%" }}></th>
                          <th style={{ width: "10%" }}></th>
                          <th style={{ width: "10%" }}></th>
                          <th style={{ width: "15%" }}></th>
                          <th style={{ width: "10%" }}></th>
                          {/* Columna de acción eliminada */}
                        </tr>
                        <tr>
                          <th colSpan="5"></th>
                          {/* Columna de acción eliminada */}
                        </tr>
                      </thead>
                      <tbody>
                        {impresion.parametros.map((parametro, paramIndex) => (
                          <>
                            <tr key={`${parametro.id || paramIndex}-row1`}>
                              <td rowSpan="2">{paramIndex + 1}</td>
                              <td>
                                <Label className="form-label fw-bold">Impresos</Label>
                                <Input
                                  type="number"
                                  value={parametro.impresos || ""}
                                  onChange={(e) => handleParametroChange(impresionIndex, paramIndex, 'impresos', e.target.value)}
                                  className="form-control form-control-sm"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Sobr.</Label>
                                <Input
                                  type="number"
                                  value={parametro.sobrantes || ""}
                                  onChange={(e) => handleParametroChange(impresionIndex, paramIndex, 'sobrantes', e.target.value)}
                                  className="form-control form-control-sm"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Total</Label>
                                <Input
                                  type="number"
                                  value={parametro.total || ""}
                                  readOnly
                                  className="form-control form-control-sm bg-light"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Tamaño</Label>
                                <Input
                                  type="text"
                                  value={getMedidaNombre(parametro.tamano) || ""}
                                  readOnly
                                  className="form-control form-control-sm bg-light"
                                  placeholder="Automático desde Planeación"
                                />
                              </td>
                              <td>
                                <Label className="form-label fw-bold">Pasadas</Label>
                                <Input
                                  type="number"
                                  value={parametro.pasadas || ""}
                                  onChange={(e) => handleParametroChange(impresionIndex, paramIndex, 'pasadas', e.target.value)}
                                  className="form-control form-control-sm"
                                />
                              </td>
                            </tr>
                            <tr key={`${parametro.id || paramIndex}-row2`}>
                              <td colSpan="5">
                                <Label className="form-label fw-bold">Tinta/Color</Label>
                                <div className="border rounded p-2" style={{ minHeight: "75px" }}>
                                  {parseTintasFromText(parametro.tinta_color || '').map((tinta, lineIndex) => (
                                    <div key={lineIndex} className="d-flex align-items-center mb-1">
                                      <Input
                                        type="select"
                                        value={tinta}
                                        onChange={(e) => changeTintaLine(impresionIndex, paramIndex, lineIndex, e.target.value)}
                                        className="form-control form-control-sm me-2"
                                        style={{ flex: 1 }}
                                      >
                                        <option value="">Seleccionar tinta...</option>
                                        {getParametrosTintaSeleccionados().map(parametroDisponible => (
                                          <option key={parametroDisponible.id} value={parametroDisponible.displayText}>
                                            {parametroDisponible.displayText}
                                          </option>
                                        ))}
                                      </Input>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeTintaLine(impresionIndex, paramIndex, lineIndex)}
                                        className="p-1"
                                        style={{ width: "30px", height: "30px" }}
                                      >
                                        <i className="fas fa-times"></i>
                                      </Button>
                                    </div>
                                  ))}
                                  {parseTintasFromText(parametro.tinta_color || '').length < getParametrosTintaSeleccionados().length && (
                                    <Button
                                      color="outline-primary"
                                      size="sm"
                                      onClick={() => addTintaLine(impresionIndex, paramIndex)}
                                      className="mt-1"
                                    >
                                      <i className="fas fa-plus me-1"></i>
                                      Agregar Tinta
                                    </Button>
                                  )}
                                  {parseTintasFromText(parametro.tinta_color || '').length === 0 && (
                                    <div className="text-muted text-center py-2">
                                      <small>No hay tintas seleccionadas</small>
                                    </div>
                                  )}
                                </div>
                              </td>
                              {/* El botón de eliminar parámetros se ha quitado ya que ahora esto se maneja desde Corte y Conversión */}
                            </tr>
                          </>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {/* El botón para agregar filas ha sido eliminado ya que ahora esto se maneja desde Corte y Conversión */}
                  <small className="text-muted mt-2 d-block">
                    Para agregar filas, utilice la sección de Corte y Conversión
                  </small>
                </Col>
              </Row>
            </Collapse>
          </div>
        ))}
        
        <div className="mt-3">
          {/* El botón para agregar nuevo tipo de papel ha sido eliminado */}
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            Los tipos de papel y sus parámetros se configuran exclusivamente desde la sección de Corte y Conversión.
            Cualquier cambio en la estructura debe realizarse desde allí.
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

Impresion.propTypes = {
  data: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  materiales: PropTypes.array.isRequired,
  medidas: PropTypes.array.isRequired,
  parametrosTinta: PropTypes.array,
  onValidationError: PropTypes.func
};

Impresion.defaultProps = {
  data: [],
  medidas: []
};

export default Impresion;
